import { defaultFormValues, SKILL_LIST } from '../constants/enquiryFormOptions';

export const FORM_DRAFT_KEY = 'dct_enquiry_form_draft';
const SESSION_KEY = 'dct_enquiry_session';

/** Map saved step from legacy wizards to current 5-step flow. */
export function normalizeDraftStep(step) {
  if (typeof step !== 'number' || step < 0) return 0;
  if (step >= 6) return 4;
  if (step === 5) return 3;
  if (step >= 3 && step <= 4) return 2;
  if (step <= 2) return step;
  return Math.min(step, 4);
}

function isDefaultYearOfPassing(value) {
  const year = Number(value);
  return !value || year === defaultFormValues.education.yearOfPassing;
}

export function hasMeaningfulDraft(formData, step = 0) {
  if (!formData) return false;
  if (step > 0) return true;

  const { personal, education, work, coursePreference, careerGoals, skillRatings, declaration } =
    formData;

  if (personal?.fullName?.trim()) return true;
  if (personal?.dateOfBirth) return true;
  if (personal?.gender) return true;
  if (personal?.mobile?.trim()) return true;
  if (personal?.whatsapp?.trim()) return true;
  if (personal?.email?.trim()) return true;
  if (personal?.address?.city?.trim()) return true;
  if (personal?.address?.state?.trim()) return true;

  if (education?.highestQualification) return true;
  if (education?.highestQualificationOther?.trim()) return true;
  if (education?.currentStatus) return true;
  if (education?.collegeName?.trim()) return true;
  if (education?.branch?.trim()) return true;
  if (!isDefaultYearOfPassing(education?.yearOfPassing)) return true;
  if (education?.cgpaOrPercentage !== '' && education?.cgpaOrPercentage != null) return true;

  if (work?.totalExperience) return true;
  if (work?.lastCompany?.trim()) return true;
  if (work?.lastDesignation?.trim()) return true;
  if (work?.knownSkills?.trim()) return true;

  if (coursePreference?.courseInterested) return true;
  if (coursePreference?.batchMode) return true;
  if (coursePreference?.preferredStartMonth?.trim()) return true;
  if (coursePreference?.hearAboutUs) return true;
  if (coursePreference?.primaryGoalAfterCourse) return true;

  if (careerGoals?.whyJoin?.trim()) return true;
  if (careerGoals?.careerGoal2To3Years?.trim()) return true;
  if (careerGoals?.lookingFor?.length) return true;
  if (careerGoals?.expectedSalaryLpa !== '' && careerGoals?.expectedSalaryLpa != null) return true;
  if (careerGoals?.willingToRelocate) return true;

  if (skillRatings?.some((r) => r.level)) return true;
  if (declaration?.accepted) return true;

  return false;
}

/** True on full page reload or first visit in a new browser tab/session. */
export function isPageReloadOrReturnVisit() {
  const nav = performance.getEntriesByType('navigation')[0];
  if (nav?.type === 'reload') return true;

  if (!sessionStorage.getItem(SESSION_KEY)) {
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  }

  return false;
}

export function shouldShowDraftRestoreNotice(draft) {
  if (!draft) return false;
  if (!hasMeaningfulDraft(draft.formData, draft.step)) return false;
  return isPageReloadOrReturnVisit();
}

function mergeSkillRatings(saved) {
  const map = new Map((saved || []).map((r) => [r.skill, r.level ?? '']));
  return SKILL_LIST.map((skill) => ({
    skill,
    level: map.get(skill) ?? '',
  }));
}

export function mergeWithDefaults(saved) {
  if (!saved || typeof saved !== 'object') {
    return defaultFormValues;
  }

  return {
    personal: {
      ...defaultFormValues.personal,
      ...saved.personal,
      address: {
        ...defaultFormValues.personal.address,
        ...saved.personal?.address,
      },
    },
    education: {
      ...defaultFormValues.education,
      ...saved.education,
    },
    work: {
      ...defaultFormValues.work,
      ...saved.work,
    },
    coursePreference: {
      ...defaultFormValues.coursePreference,
      ...saved.coursePreference,
    },
    careerGoals: {
      ...defaultFormValues.careerGoals,
      ...saved.careerGoals,
      lookingFor: Array.isArray(saved.careerGoals?.lookingFor)
        ? saved.careerGoals.lookingFor
        : defaultFormValues.careerGoals.lookingFor,
    },
    skillRatings: mergeSkillRatings(saved.skillRatings),
    declaration: {
      ...defaultFormValues.declaration,
      ...saved.declaration,
      accepted: Boolean(saved.declaration?.accepted),
    },
  };
}

export function loadFormDraft() {
  try {
    const raw = localStorage.getItem(FORM_DRAFT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const formData = mergeWithDefaults(parsed.formData);
    const step = normalizeDraftStep(parsed.step);

    return { formData, step, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function saveFormDraft(formData, step) {
  try {
    localStorage.setItem(
      FORM_DRAFT_KEY,
      JSON.stringify({
        formData,
        step,
        savedAt: new Date().toISOString(),
      })
    );
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function clearFormDraft() {
  try {
    localStorage.removeItem(FORM_DRAFT_KEY);
  } catch {
    // ignore
  }
}
